import { Link } from "react-router-dom";
import { Trophy, Timer, Target, MapPin } from "lucide-react";
import type { CompleteGameSessionResult } from "@/types/game";

interface Props {
  result: CompleteGameSessionResult;
  studentName: string;
  schoolName: string | null;
}

export default function ResultsCard({ result, studentName, schoolName }: Props) {
  return (
    <div className="card-dark p-6 max-w-md mx-auto w-full space-y-6">
      <div>
        <p className="text-sm text-steel-300">{studentName}{schoolName ? ` · ${schoolName}` : ""}</p>
        <h1 className="font-display text-3xl text-white">Your run</h1>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Stat icon={<Trophy className="w-4 h-4" />} label="Score" value={String(result.score)} />
        <Stat icon={<Target className="w-4 h-4" />} label="Correct"
              value={`${result.correct_answers} / ${result.total_questions}`} />
        <Stat icon={<Timer className="w-4 h-4" />} label="Total time" value={fmt(result.total_time_seconds)} />
        <Stat icon={<MapPin className="w-4 h-4" />} label="Texas rank"
              value={result.rank ? `#${result.rank}` : "—"} />
      </div>

      {result.missed_categories.length > 0 && (
        <div>
          <h3 className="text-sm uppercase tracking-wider text-steel-300 mb-2">Where you slipped</h3>
          <ul className="space-y-1">
            {result.missed_categories.map((m) => (
              <li key={m.category} className="flex justify-between text-sm">
                <span>{m.category}</span><span className="text-rose-300">−{m.missed}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Link to="/leaderboard" className="btn-primary w-full">
        View Texas leaderboard
      </Link>
    </div>
  );
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-xl bg-steel-900 border border-steel-700 p-3">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-steel-300">
        {icon}{label}
      </div>
      <div className="text-2xl font-display text-white mt-1">{value}</div>
    </div>
  );
}

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
